// RESEARCH GRAPH (Rust-style tech tree). `defaults` = unlocked FREE from the start (the starter survival loop + the
// bench/claim gates) — NOT tree nodes. `nodes` = researchable items, each on a workbench tier (1/2/3); a level-N bench
// researches any node with tier <= N. `reqs` are prerequisite item ids (a default req is auto-satisfied → no edge).
// `costs` = salvage cost PER ITEM, kept independent of placement: editing it in the left palette or on a tree node
// writes the same value, so dragging in / removing keeps the cost. The /admin tech-tree editor edits all of this live.
// This default layout was authored in the /admin editor (Default tab + re-tiered tree) and baked in as the baseline.
// NOTE: data + editor only — gating crafting behind research is a later ticket.
export interface ResearchNode {
  reqs: string[];   // item ids that must be researched/owned BEFORE this one
  x: number;        // free-grid position (world px)
  y: number;
  tier: number;     // workbench tier required (1/2/3)
}
export interface ResearchConfig {
  nodes: Record<string, ResearchNode>; // researchable craftables on the tree
  defaults: string[];                   // free from the start (incl. bench/claim gates) — NOT on the tree
  costs: Record<string, number>;        // salvage cost per item id (persists in palette + tree); read via costs[id] ?? 20
}

// Free from the start: starter survival loop + Stone tools + the basic Furnace + the bench/claim gates.
// research_table is FREE so the tree can be bootstrapped (you research AT a table -- gating it would deadlock).
const DEFAULTS = ['rock', 'torch', 'hammer', 'blueprint', 'wood_axe', 'wood_pickaxe', 'stone_axe', 'stone_pickaxe', 'chest', 'campfire', 'furnace', 'sleeping_bag', 'wooden_door', 'wood_window', 'water_catcher', 'wooden_lock', 'spikes', 'bench1', 'bench2', 'bench3', 'baseclaim', 'research_table'];

// Researchable nodes only: [id, tier, cost, reqs, col, row]. Layout from the /admin editor (col 3 left as a gutter).
const LAYOUT: Array<[string, number, number, string[], number, number]> = [
  ['metal_axe', 1, 120, [], 0, 0], ['metal_pickaxe', 1, 120, ['metal_axe'], 1, 0], ['oil_refinery', 3, 300, ['large_water_catcher'], 4, 0],
  ['research_table', 1, 20, [], 0, 1], ['repair_table', 1, 100, ['research_table'], 1, 1], ['bed', 2, 30, ['repair_table'], 2, 1], ['large_water_catcher', 3, 250, [], 4, 1],
  ['pistol_trap', 1, 300, [], 0, 2], ['shotgun_trap', 2, 450, ['pistol_trap'], 1, 2], ['flame_trap', 3, 250, ['shotgun_trap'], 2, 2], ['mine', 3, 200, ['flame_trap'], 4, 2],
  ['bigchest', 1, 120, [], 0, 3], ['fridge', 2, 220, ['bigchest'], 1, 3], ['drop_box', 2, 120, ['fridge'], 2, 3], ['locker', 3, 200, ['drop_box'], 4, 3],
  ['metal_door', 2, 200, [], 1, 4], ['metal_double_door', 2, 350, ['metal_door'], 2, 4], ['large_furnace', 2, 250, ['drop_box'], 4, 4],
  ['glass_window', 2, 90, [], 1, 5], ['metal_window', 2, 150, ['glass_window'], 2, 5], ['reinforced_window', 3, 300, ['metal_window'], 4, 5],
  ['keypad_lock', 2, 75, [], 1, 6], ['reinforced_door', 3, 300, ['metal_door'], 3, 6],
  // Weapons -- guns/explosives are the LOCKED tier (craftMenu + research.ts unlocks.ts); melee/bows never lock
  // but still live here so the research table can offer a cheaper unlock if the owner wants to gate them later.
  ['pistol', 1, 75, [], 0, 7], ['double_barrel', 1, 100, ['pistol'], 1, 7], ['smg', 2, 150, ['pistol'], 2, 7], ['shotgun', 2, 200, ['double_barrel'], 3, 7],
  ['ak', 3, 250, ['smg'], 4, 7], ['rifle', 3, 250, ['smg'], 0, 8], ['sniper', 3, 300, ['rifle'], 1, 8], ['rocket_launcher', 3, 350, ['rifle'], 2, 8],
  ['grenade', 2, 100, [], 3, 8], ['satchel_charge', 2, 150, ['grenade'], 4, 8], ['c4', 3, 250, ['satchel_charge'], 0, 9],
];

export const DEFAULT_RESEARCH_COST = 20; // fallback for any item with no stored cost yet

export const RESEARCH: ResearchConfig = { nodes: {}, defaults: [...DEFAULTS], costs: {} };
for (const [id, tier, cost, reqs, col, row] of LAYOUT) {
  RESEARCH.nodes[id] = { reqs, tier, x: col * 235 + 40, y: row * 132 + 40 };
  RESEARCH.costs[id] = cost;
}

// --- Research gating (the "make the tree work in-game" switch) ---------------------------------------
// When ON, EVERY tree node must be researched before it can be crafted (defaults stay free). This is the full
// Rust-style tech tree. It is a real balance shift (it gates locks, storage, doors, traps, etc.), so it's a single
// flag. Currently OFF: the research GRAPH still shows + research still works, but crafting only gates the old
// `locked` set (guns/explosives/reinforced). Flip to true to turn on the full tech-tree gate once the basic
// utility items have been placed in the free `defaults` tier so normal play isn't blocked.
export const RESEARCH_GATE_ALL = false;

const DEFAULT_SET = new Set<string>(DEFAULTS);

// Is this id a node on the research tree (as opposed to a free default / a non-craftable resource)?
export function isResearchNode(id: string): boolean {
  return Object.prototype.hasOwnProperty.call(RESEARCH.nodes, id);
}

// Are this node's prerequisites satisfied? A req is met if it is a free default or already researched.
// `unlockedFn` is injected (research.ts stays free of the unlocks localStorage module -> no import cycle).
export function reqsMet(id: string, unlockedFn: (id: string) => boolean): boolean {
  const n = RESEARCH.nodes[id];
  if (!n) return true;
  return n.reqs.every((r) => DEFAULT_SET.has(r) || unlockedFn(r));
}

// Should crafting this id be BLOCKED pending research? Only tree nodes gate, and only while GATE_ALL is on
// (otherwise the caller keeps its own narrower `locked` check). Defaults + off-tree items are never gated.
export function researchGated(id: string, unlockedFn: (id: string) => boolean): boolean {
  return RESEARCH_GATE_ALL && isResearchNode(id) && !unlockedFn(id);
}

export type ResearchState = 'researched' | 'available' | 'blocked'; // available = reqs met + not yet researched
export function researchState(id: string, unlockedFn: (id: string) => boolean): ResearchState {
  if (DEFAULT_SET.has(id) || unlockedFn(id)) return 'researched';
  return reqsMet(id, unlockedFn) ? 'available' : 'blocked';
}
